/**
 * Shared organization role definitions used across the application
 * for registration and organization settings.
 *
 * `value` ist der Datenwert (tbl_organizations.roles / tbl_location_roles,
 * Backend-Paket `orgroles`) und bleibt unveraendert. `title` ist der
 * Anzeigename — einzige Quelle fuer alle Oberflaechen (Kundenfeedback
 * 13.09.2026: "Rollenbezeichnungen anpassen/vereinheitlichen, auch bei
 * der Registrierung"). Neue Anzeigeorte nutzen `roleLabel()`, statt die
 * Beschriftung erneut zu hinterlegen.
 */

export interface OrganizationRole {
  value: string
  title: string
  iconType: string
  description: string
}

export const privateRoleItems: OrganizationRole[] = [
  {
    value: "Privatkunde",
    title: "Kunde",
    iconType: "customer",
    description: "Sie sind Geschädigter eines Verkehrsunfalls und benötigen Unterstützung bei der Abwicklung.",
  },
  {
    value: "Zeuge",
    title: "Zeuge",
    iconType: "eye",
    description: "Sie sind Zeuge eines Verkehrsunfalls und möchten bei der Abwicklung unterstützen.",
  },
];


export const organizationRoleItems: OrganizationRole[] = [
  {
    value: "Firmenkunde",
    title: "Kunde",
    iconType: "customer",
    description: "Sie sind Geschädigter eines Verkehrsunfalls und benötigen Unterstützung bei der Abwicklung.",
  },
  {
    value: "Autohaus",
    title: "Autohaus/Werkstatt",
    iconType: "car-dealer",
    description: "Sie sind Autohaus oder Werkstatt und begleiten Kunden bei Reparatur und Abwicklung.",
  },
  {
    value: "Autovermietung",
    title: "Autovermietung",
    iconType: "car-rental",
    description: "Sie sind Autovermietung und unterstützen Kunden bei der Anmietung von Fahrzeugen.",
  },
  {
    value: "Rechtsanwalt",
    title: "Rechtsanwaltskanzlei",
    iconType: "lawyer",
    description: "Sie vertreten Kunden im Schadenfall und übernehmen die rechtliche Abwicklung.",
  },
  {
    value: "Sachverständiger",
    title: "Sachverständigenbüro",
    iconType: "specialist",
    description: "Sie erstellen Gutachten und unterstützen bei der technischen Bewertung des Schadens.",
  },
  {
    value: "Abschleppdienst",
    title: "Abschleppdienst",
    iconType: "towing-service",
    description: "Sie sichern und transportieren Unfallfahrzeuge und unterstützen Kunden vor Ort.",
  },
  {
    value: "Fuhrpark",
    title: "Fuhrpark",
    iconType: "fleet",
    description: "Sie betreiben einen Fuhrpark und wickeln Schäden an Ihren eigenen Fahrzeugen ab.",
  },
  {
    value: "Versicherungsmakler",
    title: "Versicherungsmakler",
    iconType: "insurance-broker",
    description: "Sie vermitteln Versicherungen und begleiten Ihre Kunden im Schadenfall.",
  },
  {
    value: "Versicherung",
    title: "Versicherung",
    iconType: "insurance",
    description: "Sie bearbeiten Schadensmeldungen und koordinieren die Regulierung des Versicherungsfalls.",
  },
  {
    value: "Beteiligter",
    title: "Beteiligter",
    iconType: "customer",
    description: "Sie wurden fallbezogen als Beteiligter eingeladen.",
  },
]

/**
 * Rollen, die ein Unternehmen selbst wählen kann (Registrierung, Standortpflege).
 * Spiegel von backend/internal/orgroles.Registrable — die Invite-Only-Rollen
 * "Versicherung" und "Beteiligter" fehlen bewusst: beide entstehen
 * ausschließlich über fallbezogene Einladungen.
 */
export const registrableRoleItems: OrganizationRole[] = organizationRoleItems.filter(
  (r) => r.value !== "Versicherung" && r.value !== "Beteiligter",
);

/** Alle Unternehmensrollen für Routen- und Navigationsfreigaben. */
export const COMPANY_ROLE_VALUES: string[] = organizationRoleItems.map((r) => r.value);

/**
 * Anzeigename einer Rolle. Deckt Unternehmens- und Privatrollen ab; unbekannte
 * Werte werden unveraendert zurueckgegeben, damit fallbezogene Sonderrollen
 * nicht als leerer Text erscheinen.
 */
export function roleLabel(value: string): string {
  // Kundenfeedback 22.09.26 (Teil 2), X: Das Backend führt den Mandanten
  // fallbezogen als technische Rolle "client" – angezeigt wird „Kunde".
  const technical = CASE_ROLE_LABELS[String(value ?? "").trim().toLowerCase()];
  if (technical) return technical;
  const item =
    organizationRoleItems.find((r) => r.value === value) ??
    privateRoleItems.find((r) => r.value === value);
  return item?.title ?? value;
}

/** Technische Fallrollen (Backend) → Anzeigename. */
const CASE_ROLE_LABELS: Record<string, string> = {
  client: "Kunde",
  mandant: "Kunde",
};

/**
 * Rollenliste eines Beteiligten als Anzeigetext, z. B. ["client"] → "Kunde",
 * ["Autohaus", "Werkstatt"] → "Autohaus, Werkstatt". Doppelte Anzeigenamen
 * werden zusammengefasst; leere Liste → fallback.
 */
export function participantRolesLabel(roles: unknown, fallback = ""): string {
  const list = Array.isArray(roles) ? roles : typeof roles === "string" && roles ? [roles] : [];
  // Unternehmensrollen bleiben wie gespeichert („Rechtsanwalt", nicht
  // „Rechtsanwaltskanzlei") – übersetzt werden nur technische Fallrollen.
  const labels = [...new Set(list.map((r) => {
    const raw = String(r ?? "").trim();
    return CASE_ROLE_LABELS[raw.toLowerCase()] ?? raw;
  }).filter(Boolean))];
  return labels.length > 0 ? labels.join(", ") : fallback;
}
